import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { RewardInfoApis } from 'apis/RewardInfoApis';
import { PlusWhiteIcon } from 'assets/svg';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonImport from 'components/Button/ButtonImport';
import EditRewardIcon from 'components/Icons/EditRewardIcon';
import { UploadModal } from 'components/UploadModal_V2';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import useDefaultFilterReward from 'hooks/useDefaultFilterReward';
import useQueryParam from 'hooks/useQueryPrams';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { toastError, toastSuccess } from 'utils/constants/functions';
import {
  IMPORT_EDIT_EMPLOYEE,
  IMPORT_REWARD_INFO,
} from 'utils/constants/importId';
import FilterRewardInfo from './FilterRewardInfo';
import TableRewardInfo from './TableRewardInfo';

const IMPORT_EDIT = 1;
const IMPORT_CREATE = 2;
const NO_ACTION = 0;

const RewardInfoPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { setTitle, setBreadcrumb } = useTitle();
  const childRef = useRef<any>(null);
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [formParams, setFormParams] = useState<any>({});
  const [importType, setImportType] = useState<number>(NO_ACTION);

  const [formTable] = useForm();
  const [isAdd, setIsAdd] = useState<boolean>(false);
  const [dataTable, setDataTable] = useState<any>({});
  const [indexHandle, setIndexHandle] = useState<number>(-1);
  const [isOpenImportModal, setIsOpenImportModal] = useState<boolean>(false);

  const queryParam = useQueryParam();
  const page = parseInt(queryParam.get('page') + '') || 1;
  const page_size =
    parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const defaultFilter = useDefaultFilterReward();

  const mutateGetRewardInfo = useMutation({
    mutationFn: RewardInfoApis.getRewardInfo,
    onSuccess: (res, variables) => {
      const result = res?.data?.getUserRewardData || {};
      // console.log(res);
      if (variables.type === 'export') {
        exportExcel_v2(
          variables.headerRow,
          result.items || [],
          'Thông tin nhân viên',
        );
      } else {
        setDataTable({ ...result });
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const {
    mutate: mutateCreateNewUserReward,
    isPending: isPendingCreateNewUserReward,
  } = useMutation({
    mutationFn: RewardInfoApis.createNewUserReward,
    onSuccess: (res: any) => {
      if (res.errors?.length > 0) {
        toastError(res.errors[0].message);
        return;
      }

      toastSuccess('Tạo mới thành công');
      refetchGetRewardInfo();
      handleCancel();
    },
    onError: (error) => {
      toastError('Tạo mới thất bại');
    },
  });

  const {
    mutate: mutateUpdateUserRewardById,
    isPending: isPendingUpdateUserRewardById,
  } = useMutation({
    mutationFn: RewardInfoApis.updateUserRewardById,
    onSuccess: () => {
      toastSuccess('Cập nhật thành công');
      refetchGetRewardInfo();
      handleCancel();
    },
    onError: (error) => {
      toastError('Cập nhật thất bại');
    },
  });

  const refetchGetRewardInfo = useCallback(
    async (isExport?: boolean, headerRow?: any) => {
      try {
        await mutateGetRewardInfo.mutateAsync({
          page: isExport ? 1 : page,
          page_size: isExport
            ? dataTable?.pageInfo?.totalItems || 1000
            : page_size,
          ...(defaultFilter && {
            ...defaultFilter,
            plantId: defaultFilter?.plantId
              ? defaultFilter?.plantId.replace('all', '')
              : undefined,
            user: defaultFilter?.employeeId,
          }),
          ...(formParams && formParams),
          ...(isExport && {
            headerRow,
            type: 'export',
          }),
        });
      } catch (error) {
        console.log('mutateGetRewardInfo', error);
      }
    },
    [
      page,
      dataTable?.pageInfo?.totalItems,
      page_size,
      defaultFilter,
      formParams,
    ],
  );

  useEffect(() => {
    refetchGetRewardInfo();
  }, [refetchGetRewardInfo]);

  useEffect(() => {
    setTitle('Thông tin nhân viên');
    setBreadcrumb([
      { title: 'Doanh thu/ Thưởng' },
      { title: 'Thông tin nhân viên' },
    ]);
  }, [setTitle, setBreadcrumb]);

  const handleExportExcel = () => {
    if (childRef.current) {
      childRef.current.exportExcel();
    }
  };

  const handleImport = (type: number) => {
    setIsOpenImportModal(true);
    setImportType(type);
  };

  const handleOkFilter = useCallback(
    (values: any) => {
      console.log(values);
      navigate(`?page=1&page_size=${page_size}`);
      setFormParams({
        ...values,
        plantId: values?.plantId
          ? values?.plantId.replace('all', '')
          : undefined,
      });
    },
    [page_size],
  );

  const handleCancelFilter = useCallback(() => {
    setFormParams({});
    navigate(`?page=1&page_size=${page_size}`);
  }, [navigate, page_size]);

  const handleCancel = () => {
    setIsAdd(false);
    setIndexHandle(-1);
    formTable.resetFields();
  };

  const handleAddRow = () => {
    setIsAdd(true);
    setIndexHandle(0);
    setDataTable((pre: any) => ({
      ...pre,
      items: [{ id: Date.now() }].concat(pre.items || []),
    }));
  };

  const handleCancelTable = () => {
    if (isAdd) {
      setDataTable((pre: any) => ({
        ...pre,
        items: (pre?.items || []).slice(1),
      }));
    }
    handleCancel();
  };

  const validateForm = () => {
    const values = formTable.getFieldsValue();

    if (isAdd) {
      if (!values.id) return toastError('Vui lòng nhập mã nhân viên');
      if (!values.name) return toastError('Vui lòng nhập tên nhân viên');
      if (!values.groupRoleManagementId)
        return toastError('Vui lòng chọn chức vụ');
      if (!values.jobTitleCode) return toastError('Vui lòng chọn chức vụ');
    }
    if (!values.signDate) return toastError('Vui lòng nhập ngày ký HĐ');
    if (!isAdd) {
      // if (!values.retireDate) return toastError('Vui lòng nhập ngày nghỉ việc');
      if (typeof values.useYn !== 'boolean')
        return toastError('Vui lòng chọn trạng thái');
    }
    // if (!values.note) return toastError('Vui lòng nhập ghi chú');
    if (isAdd) {
      if (!values.plantId) return toastError('Vui lòng chọn chi nhánh');
    }

    return true;
  };

  const handleSaveTable = () => {
    const values = formTable.getFieldsValue();
    delete values.jobName;
    values.signDate = values.signDate
      ? dayjs(values.signDate).format('YYYY-MM-DD')
      : undefined;

    if (!validateForm()) return;

    if (isAdd) {
      mutateCreateNewUserReward({
        ...values,
        useYn: true,
      });
    } else {
      const data = {
        ...dataTable.items[indexHandle],
        ...values,
        retireDate: values.retireDate
          ? dayjs(values.retireDate).format('YYYY-MM-DD')
          : undefined,
      };

      mutateUpdateUserRewardById({
        groupRoleManagementId: data.groupRoleManagementId,
        id: data.id,
        name: data.name,
        retireDate: data.retireDate,
        signDate: data.signDate,
        useYn: data.useYn,
        note: data.note,
        plantId: data.plantId,
        jobTitleCode: data.jobTitleCode,
      });
    }
  };

  const handleEditRow = (index: number) => {
    setIndexHandle(index);
    setIsAdd(false);
    const row = dataTable.items[index];
    formTable.setFieldsValue({
      ...row,
      signDate: row.signDate ? dayjs(row.signDate) : undefined,
      retireDate: row.retireDate ? dayjs(row.retireDate) : undefined,
      useYn: Boolean(row.useYn),
    });
  };

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter
            isOpen={isOpenFilter}
            setIsOpen={setIsOpenFilter}
            disabled={isAdd || indexHandle !== -1}
          />
        </Col>
        <Col>
          <ButtonExport
            onClick={handleExportExcel}
            disabled={isAdd || indexHandle !== -1}
          />
        </Col>
        <Col>
          <ButtonImport
            onClick={() => handleImport(IMPORT_CREATE)}
            disabled={
              isAdd ||
              indexHandle !== -1 ||
              !(user?.isAdmin || user?.isHO || user?.isNS)
            }
          />
        </Col>
        <Col>
          <Button
            title="Import Chỉnh sửa"
            iconBefore={<EditRewardIcon />}
            disabled={
              isAdd ||
              indexHandle !== -1 ||
              !(user?.isAdmin || user?.isHO || user?.isNS)
            }
            handleOnclick={() => handleImport(IMPORT_EDIT)}
          />
        </Col>
        <Col>
          <Button
            fill
            title="Tạo mới NV"
            iconBefore={<PlusWhiteIcon />}
            disabled={
              isAdd ||
              indexHandle !== -1 ||
              !(user?.isAdmin || user?.isHO || user?.isNS)
            }
            handleOnclick={handleAddRow}
          />
        </Col>
      </Row>
      <FilterRewardInfo
        disabled={isAdd || indexHandle !== -1}
        isOpen={isOpenFilter}
        defaultFilter={defaultFilter}
        onFinish={handleOkFilter}
        onCancel={handleCancelFilter}
      />
      <TableRewardInfo
        ref={childRef}
        form={formTable}
        data={dataTable}
        isAdd={isAdd}
        // setIsAdd={setIsAdd}
        indexHandle={indexHandle}
        // setIndexHandle={setIndexHandle}
        onCancel={handleCancelTable}
        onEdit={handleEditRow}
        onSave={handleSaveTable}
        loading={
          mutateGetRewardInfo.isPending ||
          isPendingCreateNewUserReward ||
          isPendingUpdateUserRewardById
        }
        refetch={refetchGetRewardInfo}
      />
      <UploadModal
        importId={
          importType === IMPORT_CREATE
            ? IMPORT_REWARD_INFO
            : IMPORT_EDIT_EMPLOYEE
        }
        templateFileName="Thông tin nhân viên"
        open={isOpenImportModal}
        title={
          importType === IMPORT_CREATE
            ? 'Import Thông tin nhân viên'
            : 'Import Chỉnh sửa Thông tin nhân viên'
        }
        setOpen={setIsOpenImportModal}
        refetch={refetchGetRewardInfo}
      />
    </>
  );
};

export default RewardInfoPage;
